import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "../../components/enterprise2/MarketingLayout";
import { TwoCol } from "../../components/enterprise2/TwoCol";
import { HairlineList } from "../../components/enterprise2/HairlineList";
import { Accordion } from "../../components/enterprise2/Accordion";


const STACK_LAYERS = [
  { name: "Applied AI Foundations", desc: "In-country compute and edge hardware, so the intelligence runs on ground you control." },
  { name: "Crowther Cloud", desc: "Provisioning, orchestration, and model serving inside your own borders." },
  { name: "Atom", desc: "Four specialist models: Electron the fast edge model, Proton the enterprise workhorse, Neutron the retrieval model, Nucleus the flagship, tuned to your languages, regulations, and workflows." },
  { name: "Foundation", desc: "Governed platform with entity resolution at its core, permissions, audit trails, review loops." },
  { name: "Realloc", desc: "Every deployment scored against one constraint, that it elevate more human work than it displaces." },
];

const SECTORS = [
  { name: "Financial Services", desc: "Banks, capital markets, and payments infrastructure that must keep customer data in-country and prove it." },
  { name: "Insurance", desc: "Life, health, and general insurers running claims, underwriting, and reserving on data that must stay home." },
  { name: "Telecommunications", desc: "Operators processing subscriber data under national regulation, with agentic operations across a very large plant." },
  { name: "Government and Public Sector", desc: "Ministries, agencies, and central banks. Sovereign infrastructure is a hard requirement, not a preference." },
  { name: "Healthcare and Life Sciences", desc: "Providers and networks holding patient records that must never leave the jurisdiction they were created in." },
  { name: "Pharmaceuticals", desc: "Research data and clinical operations under regulator and ethics-committee oversight, in-country." },
  { name: "Energy and Utilities", desc: "Grid, generation, and network operators with critical infrastructure telemetry that stays inside national borders." },
  { name: "Logistics and Supply Chain", desc: "Cross-border operators whose data must respect several regimes at once, with a resolvable entity view across them." },
];

const FAQ = [
  { q: "Who is this built for?", a: "Regulated institutions and their operators. Banks, insurers, telcos, health systems, ministries, energy companies, logistics operators. Anywhere the data is sensitive, the regulator is present, and the decisions must be traceable." },
  { q: "What does sovereignty mean in practice?", a: "Compute, storage, model serving, and encryption keys sit inside your own environment. The stack runs in-country, in-region, on infrastructure you control. Ownership transfers to you under the build-and-transfer model. There is no operational path by which your data leaves your jurisdiction." },
  { q: "How is it deployed, and who ends up owning it?", a: "We build the whole stack into your environment, run it into production with you, and hand it over for you to own. Most institutions enter through a single high-value problem and expand across the stack as trust deepens." },
  { q: "Does it work with our existing systems?", a: "Yes. Foundation connects to your systems of record rather than replacing them, reads under the access model you define, and resolves records into entities without lifting them out." },
  { q: "Where does our data live, and who controls it?", a: "In your environment, in your region, under your governance. Access is granted and revoked by you. Encryption keys are held by you. The resolved knowledge that accumulates over time is yours to own." },
];

export default function Solution() {
  return (
    <MarketingLayout
      active="solution"
      title="What we build. Sovereign intelligence for regulated institutions. Crowther."
      description="Crowther builds and owns the entire stack a regulated enterprise needs to run artificial intelligence on its own ground, from the compute underneath to the governed platform on top."
    >
      {/* Hero */}
      <section className="e2-hero" data-testid="e2-solution-hero" style={{ minHeight: "auto", paddingTop: 96, paddingBottom: 24 }}>
        <div className="e2-eyebrow">What We Build</div>
        <h1 className="e2-h-xl" style={{ marginTop: 26, maxWidth: "24ch" }}>
          Sovereign intelligence for the institutions that answer to a regulator.
        </h1>
        <p className="e2-body e2-body-lg" style={{ marginTop: 32, maxWidth: "60ch" }}>
          Crowther builds and owns the entire stack a regulated enterprise needs to run artificial intelligence on its own ground, from the compute underneath to the governed platform on top. We build it into your environment, in-country, and hand it over for you to own.
        </p>
        <div style={{ marginTop: 36, display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
          <Link to="/plan-2" className="e2-btn e2-btn-primary" data-testid="e2-solution-cta">
            Get Your Firm&rsquo;s Free Customised AI Roadmap
          </Link>
          <Link to="/enterprise-2/atom" className="e2-btn e2-btn-link" data-testid="e2-solution-atom">
            Explore Atom
          </Link>
        </div>
      </section>

      {/* The problem */}
      <TwoCol
        id="e2-solution-problem"
        label="The problem"
        title="Global AI was never designed with regulated markets in mind."
      >
        <div className="e2-stack-24">
          <p className="e2-body">
            The world&rsquo;s frontier compute concentrates in a small handful of countries. For most institutions, in most jurisdictions, that has meant a choice between sending sensitive data to systems they do not own, or staying on the sidelines while the technology moves on without them.
          </p>
          <p className="e2-body">
            The rules have caught up. Data residency and localisation rules were in force across more than one hundred and fifty countries by the end of 2024, and the trajectory is towards more, not fewer. Sensitive records must stay inside national borders. Regulated firms must be able to show a regulator exactly where their data lives and who touches it.
          </p>
          <p className="e2-body">
            You need intelligence built to run under your constraints rather than around them. That is what we build.
          </p>
        </div>
      </TwoCol>

      {/* What you get */}
      <TwoCol
        id="e2-solution-stack"
        label="What you get"
        title="One sovereign stack, yours from the metal to the application."
      >
        <HairlineList rows={STACK_LAYERS} />
        <p className="e2-body" style={{ marginTop: 32 }}>Five layers, one owner. Yours, in the end.</p>
        <p className="e2-body-sm" style={{ marginTop: 16, color: "var(--slate)" }}>
          Some layers are in build today; every engagement is scoped to what is production-ready, and we will tell you which is which.
        </p>
      </TwoCol>

      {/* The difference */}
      <section className="e2-band" data-testid="e2-solution-difference">
        <div className="e2-shell">
          <div className="e2-label" style={{ marginBottom: 20 }}>The difference</div>
          <h2 className="e2-h-m">We do more than find your records. We make sense of them.</h2>
          <p className="e2-body" style={{ marginTop: 24, maxWidth: "72ch" }}>
            Most systems retrieve documents. They find the pages that mention a name and hand them back. Foundation goes further. It resolves the records scattered across your core systems into the single true entity behind them, the one customer, the one counterparty, the one account, however many ways it appears across your data. Reasoning then runs over the relationships between those entities, not only the documents that mention them. This is the part a general tool cannot shortcut, and it is where the durable value of the deployment lives.
          </p>
        </div>
      </section>

      {/* How you own it */}
      <TwoCol
        id="e2-solution-ownership"
        label="How you own it"
        title="We build it with you, then hand you the keys."
      >
        <p className="e2-body">
          We build the stack into your own environment, hosted in-region to meet your data-residency requirements, and then hand it over for you to own and run. You are not buying access to a capability held somewhere else, with the risk that the terms change or the access is withdrawn. You own the deployment, the infrastructure, and the resolved knowledge it produces. Most institutions enter through a single high-value problem and expand across the stack as the relationship deepens.
        </p>
      </TwoCol>

      {/* Where it runs */}
      <section className="e2-band" data-testid="e2-solution-where">
        <div className="e2-shell">
          <div className="e2-label" style={{ marginBottom: 20 }}>Where it runs</div>
          <h2 className="e2-h-m">In your country, under your ownership, answerable to your regulator.</h2>
          <p className="e2-body" style={{ marginTop: 24, maxWidth: "72ch" }}>
            Your data lives inside your own environment, in-region, under your control. Access is enforced at the moment records are resolved rather than added afterward. The resolved knowledge that accumulates over time belongs to your institution, not to us, and it never leaves your control. This is Compute Residency and Enterprise Sovereignty made concrete: a moat you build and own, not one you rent.
          </p>
        </div>
      </section>

      {/* Who we serve */}
      <TwoCol
        id="e2-solution-sectors"
        label="Who we serve"
        title="Designed for regulated institutions from day one."
        lede="Useful anywhere data must stay and decisions must be traceable."
      >
        <HairlineList rows={SECTORS} />
        <p className="e2-body-sm" style={{ marginTop: 32, color: "var(--slate)" }}>
          Named references from live deployments are available under nondisclosure on request.
        </p>
      </TwoCol>

      {/* Commitment */}
      <section className="e2-band" data-testid="e2-solution-commitment">
        <div className="e2-shell">
          <div className="e2-label" style={{ marginBottom: 20 }}>The commitment</div>
          <h2 className="e2-h-m">Every deployment should lift more human work than it replaces.</h2>
          <p className="e2-body" style={{ marginTop: 24, maxWidth: "72ch" }}>
            This is our one design constraint, and it is measured, not asserted. Realloc assesses work at the level of tasks rather than job titles and returns clear verdicts on what to retain, retrain, or redeploy, with the elevation constraint built into how the assessment is scored. You get intelligence that raises the ceiling on what your people do, rather than a system that quietly hollows out the work and calls it efficiency.
          </p>
        </div>
      </section>

      {/* Why it holds */}
      <TwoCol
        id="e2-solution-moat"
        label="Why it holds"
        title="An advantage you own, not one you rent."
      >
        <p className="e2-body">
          The advantage that lasts is not a feature, because features are copied within a quarter. It is the resolved knowledge that accumulates inside your own systems over years, owned by your institution, and the fact that the whole stack runs on ground you control. The longer we run inside an institution, the harder it becomes for anyone to stand between it and its own intelligence.
        </p>
      </TwoCol>

      {/* FAQ */}
      <TwoCol
        id="e2-solution-faq"
        label="Due diligence"
        title="The questions we are asked most often."
      >
        <Accordion items={FAQ} />
      </TwoCol>

      {/* Close */}
      <section className="e2-band" data-testid="e2-solution-close">
        <div className="e2-shell" style={{ textAlign: "left" }}>
          <h2 className="e2-h-m" style={{ maxWidth: "24ch" }}>The real test is deployment, not the demo.</h2>
          <p className="e2-body" style={{ marginTop: 20, maxWidth: "60ch" }}>
            Have a question that is not covered here? Reach out and we will respond within one business day.
          </p>
          <div style={{ marginTop: 32, display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <Link to="/plan-2" className="e2-btn e2-btn-primary" data-testid="e2-solution-close-cta">
              Get Your Firm&rsquo;s Free Customised AI Roadmap
            </Link>
            <Link to="/enterprise-2/contact" className="e2-btn e2-btn-link" data-testid="e2-solution-contact-link">
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
